import {
  faArrowsRotate,
  faCheckCircle,
  faTimesCircle,
  faTriangleExclamation,
  faUserPen
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { memo, useContext, useEffect, useRef, useState } from 'react';

import PrerequisiteStatuses from '@/components/AppointmentType/components/PrerequisiteStatuses';
import ScheduleExpectationCycle from '@/components/AppointmentType/components/ScheduleExpectationCycle';
import Debug from '@/components/Debug';
import { Color } from '@/constants/color';
import AppContext from '@/contexts/appContext';
import ScheduleExpectationStatusContext from '@/contexts/ScheduleExpectationStatusContext';
import useNavigateToPage from '@/hooks/useNavigateToPage';
import { useActiveCustomScheduleExpectations } from '@/hooks/useScheduleExpectations';
import PersonContext from '@/pages/Person/PersonContext';
import AppointmentsRepository from '@/repositories/AppointmentsRepository';
import { scheduleExpectationRepository } from '@/repositories/ScheduleExpectationRepository';
import type {
  PrerequisiteStatus,
  ScheduleExpectationCycle as ScheduleExpectationCycleType
} from '@/types/scheduleExpectation';
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Alert,
  Button,
  Pill,
  Tooltip
} from '@/ui';
import Modal from '@/ui/components/Modal';
import isStandaloneCustomExpectation from '@/utils/isStandaloneCustomExpectation';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

import type { Props } from './types';

const shouldHideCycleFromList = (cycle: ScheduleExpectationCycleType) => {
  return cycle.expected_this_cycle === 0 && cycle.appointments_scheduled === 0;
};

const AppointmentType = ({
  appointmentType,
  isActive = false,
  onSeeExpectationDetails,
  phase,
  timeZone,
  uiMode = 'default'
}: Props) => {
  const {
    all_expectations_met,
    appointment_type_duration,
    prerequisite_data,
    schedule_expectation_cycles
  } = appointmentType;

  const { addMessage, host } = useContext(AppContext);
  const { person } = useContext(PersonContext);
  const { navigateToAppointment } = useNavigateToPage();
  const { data: activeCustomExpectations } = useActiveCustomScheduleExpectations({
    phaseId: phase.id
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const { status: processingStatus, appointmentTypeIds: processingTypeIds } = useContext(
    ScheduleExpectationStatusContext
  );
  const isAffectedByProcessing =
    (processingStatus === 'processing' || processingStatus === 'complete') &&
    (processingTypeIds === null || processingTypeIds.includes(appointmentType.appointment_type_id));

  const [ignoreTarget, setIgnoreTarget] = useState<PrerequisiteStatus | null>(null);
  const [showIgnoreConfirm, setShowIgnoreConfirm] = useState(false);
  const [existingExpectationId, setExistingExpectationId] = useState<string | undefined>(undefined);

  const executeIgnore = useMutation({
    mutationFn: async ({
      prereq,
      existingId
    }: {
      prereq: PrerequisiteStatus;
      existingId?: string;
    }) => {
      if (existingId) {
        await scheduleExpectationRepository.deleteCustomScheduleExpectation({
          host,
          id: existingId
        });
      }
      const startNodes = await scheduleExpectationRepository.getCustomExpectationStartNodes({
        host,
        phaseId: phase.id,
        appointmentTypeId: prereq.appointment_type_id
      });
      const startAt = startNodes.min_date;
      if (!startAt) throw new Error('No valid start date available');
      return await scheduleExpectationRepository.createCustomScheduleExpectation({
        host,
        data: {
          appointment_type: prereq.appointment_type_id,
          phase: phase.id,
          cycle_time: '4',
          frequency: '0',
          look_ahead_cycles: '0',
          max_attendances: null,
          note: null,
          start_at: startAt,
          time_unit: 'week'
        }
      });
    },
    onSuccess: () => {
      addMessage({ level: 'success', text: 'Prerequisite ignored successfully' });
      setIgnoreTarget(null);
      setExistingExpectationId(undefined);
      setShowIgnoreConfirm(false);
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
      setIgnoreTarget(null);
      setExistingExpectationId(undefined);
      setShowIgnoreConfirm(false);
    }
  });

  const checkIgnore = useMutation({
    mutationFn: async (prereq: PrerequisiteStatus) => {
      const expectations = await scheduleExpectationRepository.listActiveCustomScheduleExpectations(
        {
          host,
          phaseId: phase.id
        }
      );
      const existing = expectations.find(
        (e) => e.appointment_type_id === prereq.appointment_type_id
      );
      return { prereq, existingId: existing?.id };
    },
    onSuccess: ({ prereq, existingId }) => {
      if (existingId) {
        setIgnoreTarget(prereq);
        setExistingExpectationId(existingId);
        setShowIgnoreConfirm(true);
      } else {
        executeIgnore.mutate({ prereq });
      }
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const isIgnoring = checkIgnore.isPending || executeIgnore.isPending;

  const handleConfirmIgnore = () => {
    if (!ignoreTarget) return;
    executeIgnore.mutate({ prereq: ignoreTarget, existingId: existingExpectationId });
  };

  useEffect(() => {
    if (isActive && containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [isActive]);

  const activeClasses = isActive ? 'ring-2 ring-blue-400' : '';

  const isMutedUIMode = uiMode === 'muted';
  const isDefaultUIMode = uiMode === 'default';

  const prerequisiteStatuses = prerequisite_data ?? [];
  const prerequisitesNotMet =
    prerequisiteStatuses.length > 0 &&
    !prerequisiteStatuses.some((p) => p.met_at !== null || p.waived);

  // Only cycles where expected_this_cycle === 0 are safe to bulk-cancel; appointments
  // scheduled against a non-zero expectation may still be legitimately needed.
  const cancellableOverscheduledAppointments = schedule_expectation_cycles
    .filter(
      (cycle) =>
        cycle.appointments_scheduled > cycle.expected_this_cycle && cycle.expected_this_cycle === 0
    )
    .flatMap((cycle) =>
      cycle.appointment_attendances
        ? cycle.appointment_attendances
            .filter((attendance) => !attendance.cancelled)
            .map((attendance) => attendance.appointment_attendance_id)
        : []
    );

  const appointmentsRepository = AppointmentsRepository();

  const cancelAllOverscheduled = useMutation({
    mutationFn: async (appointmentIds: string[]) => {
      return await appointmentsRepository.cancelAppointments(appointmentIds);
    },
    onSuccess: (data) => {
      addMessage({ level: 'success', text: `Cancelled ${data.length} overscheduled appointments` });
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const totalOverscheduledCount = schedule_expectation_cycles.reduce((acc, cycle) => {
    if (!cycle.silenced_at && cycle.appointments_scheduled > cycle.expected_this_cycle) {
      return acc + (cycle.appointments_scheduled - cycle.expected_this_cycle);
    }
    return acc;
  }, 0);

  const totalUnderscheduledCount = schedule_expectation_cycles.reduce((acc, cycle) => {
    if (!cycle.silenced_at && cycle.appointments_scheduled < cycle.expected_this_cycle) {
      return acc + (cycle.expected_this_cycle - cycle.appointments_scheduled);
    }
    return acc;
  }, 0);

  const totalSilencedCount = schedule_expectation_cycles.filter(
    (cycle) => cycle.silenced_at !== null
  ).length;

  // The muted view shows the complete historical record of every cycle, so
  // zero/zero cycles must stay visible there even though they're hidden from
  // the default actionable-schedule view.
  const visibleScheduleExpectationCycles = isMutedUIMode
    ? schedule_expectation_cycles
    : schedule_expectation_cycles.filter((cycle) => !shouldHideCycleFromList(cycle));
  // A zero-frequency custom expectation has no visible cycles, so also check the active ones.
  const hasActiveCustomExpectation = !!activeCustomExpectations?.some(
    (expectation) =>
      expectation.appointment_type_id === appointmentType.appointment_type_id &&
      !expectation.sequence
  );
  const hasCustomExpectation =
    hasActiveCustomExpectation ||
    visibleScheduleExpectationCycles.some((cycle) =>
      isStandaloneCustomExpectation(cycle.schedule_expectation)
    );
  // The trigger's aria-label overrides its contents, so the icon's meaning must be in it.
  const triggerLabel = hasCustomExpectation
    ? `${appointmentType.appointment_type_name}, contains a custom expectation`
    : appointmentType.appointment_type_name;
  const mostRecentVisibleScheduleExpectationCycleIndex =
    visibleScheduleExpectationCycles.length - 1;

  const title = appointment_type_duration
    ? `${appointmentType.appointment_type_name} (${appointment_type_duration} min)`
    : appointmentType.appointment_type_name;

  const v2AccordionColor = isMutedUIMode
    ? ('neutral' as const)
    : prerequisitesNotMet
      ? ('warning' as const)
      : all_expectations_met
        ? ('primary' as const)
        : ('destructive' as const);

  return (
    <div ref={containerRef} className={`relative rounded-lg ${activeClasses}`}>
      <AccordionItem color={v2AccordionColor} variant="subtle" defaultOpen={isActive}>
        <AccordionTrigger aria-label={triggerLabel}>
          <div className="flex flex-col items-start gap-1 min-w-0 text-left">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{title}</span>
              {hasCustomExpectation && (
                <Tooltip
                  content="This contains a custom expectation"
                  triggerClassName="inline-flex items-center">
                  <FontAwesomeIcon
                    icon={faUserPen}
                    className="text-xs text-uniform-gray-400 dark:text-uniform-gray-500"
                    aria-label="Custom expectation"
                  />
                </Tooltip>
              )}
              {isAffectedByProcessing && (
                <span className="text-sm text-uniform-gray-500 flex items-center gap-1.5">
                  <FontAwesomeIcon
                    icon={faArrowsRotate}
                    className={processingStatus === 'processing' ? 'animate-spin' : ''}
                  />
                  {processingStatus === 'processing' ? 'Processing...' : 'Reprocessing complete'}
                </span>
              )}
            </div>
            {isDefaultUIMode && (
              <div className="text-sm grid gap-x-1.5 gap-y-0.5 items-center [grid-template-columns:auto_1fr]">
                <FontAwesomeIcon
                  icon={
                    all_expectations_met && !prerequisitesNotMet ? faCheckCircle : faTimesCircle
                  }
                  className={`justify-self-center ${all_expectations_met && !prerequisitesNotMet ? 'text-green-500' : 'text-red-500'}`}
                />
                <span>
                  {prerequisitesNotMet
                    ? 'Prerequisites not met'
                    : all_expectations_met
                      ? 'All expectations met'
                      : 'Schedule Needs Review'}
                </span>
                {totalOverscheduledCount > 0 && (
                  <>
                    <Pill size="sm" color="destructive" variant="outline">
                      {totalOverscheduledCount}
                    </Pill>
                    <span>Overscheduled</span>
                  </>
                )}
                {totalUnderscheduledCount > 0 && (
                  <>
                    <Pill size="sm" color="destructive" variant="outline">
                      {totalUnderscheduledCount}
                    </Pill>
                    <span>Underscheduled</span>
                  </>
                )}
                {totalSilencedCount > 0 && (
                  <>
                    <Pill size="sm" color="neutral" variant="outline">
                      {totalSilencedCount}
                    </Pill>
                    <span>Silenced</span>
                  </>
                )}
                {appointmentType.self_schedule_disabled && (
                  <>
                    <FontAwesomeIcon
                      icon={faTriangleExclamation}
                      className="justify-self-center text-yellow-500 text-xs"
                      aria-label="Self-scheduling disabled for this member"
                    />
                    <span>Self-scheduling disabled</span>
                  </>
                )}
              </div>
            )}
          </div>
        </AccordionTrigger>
        <AccordionContent className="text-uniform-gray-900 dark:text-uniform-gray-100">
          {appointmentType.self_schedule_disabled && (
            <div className="relative mb-2">
              <Alert variant={isMutedUIMode ? 'info' : 'warning'}>
                {isMutedUIMode ? (
                  <>
                    Historical mode is enabled. Please turn it off in order to view disabled
                    warnings.
                  </>
                ) : (
                  <div>
                    <p className="font-medium">
                      Self scheduling is disabled for this appointment type:
                    </p>
                    {appointmentType.self_schedule_disabled_reasons.map((reason) => (
                      <p key={reason.code}>{reason.description}</p>
                    ))}
                  </div>
                )}
              </Alert>
            </div>
          )}

          <PrerequisiteStatuses
            prerequisiteStatuses={prerequisiteStatuses}
            onNavigateToAppointment={(appointmentId) =>
              navigateToAppointment(person.id, appointmentId)
            }
            onNavigateToWaivedExpectation={
              onSeeExpectationDetails
                ? (appointmentTypeId, cycleId) =>
                    onSeeExpectationDetails(appointmentTypeId, cycleId)
                : undefined
            }
            onIgnore={!isMutedUIMode ? (prereq) => checkIgnore.mutate(prereq) : undefined}
            isIgnoring={isIgnoring}
          />

          <div>
            <div className="gap-y-4">
              {visibleScheduleExpectationCycles.length > 0 ? (
                visibleScheduleExpectationCycles.map((scheduleExpectationCycle, index) => (
                  <ScheduleExpectationCycle
                    isMostRecent={index === mostRecentVisibleScheduleExpectationCycleIndex}
                    key={scheduleExpectationCycle.cycle_number}
                    memberTimeZone={timeZone}
                    onSeeExpectationDetails={onSeeExpectationDetails}
                    phaseId={phase.id}
                    scheduleExpectationCycle={scheduleExpectationCycle}
                    appointmentType={appointmentType}
                    uiMode={uiMode}
                  />
                ))
              ) : (
                <p className="text-sm text-uniform-gray-500 dark:text-uniform-gray-400">
                  No actionable cycles for this appointment type.
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-row gap-3">
            {!isMutedUIMode && cancellableOverscheduledAppointments.length > 0 && (
              <Button
                variant="default"
                color="destructive"
                size="sm"
                disabled={cancelAllOverscheduled.isPending || cancelAllOverscheduled.isSuccess}
                onClick={() => cancelAllOverscheduled.mutate(cancellableOverscheduledAppointments)}>
                Cancel all overscheduled ({cancellableOverscheduledAppointments.length})
              </Button>
            )}
          </div>

          <Debug object={appointmentType} />
        </AccordionContent>
      </AccordionItem>

      <Modal
        open={showIgnoreConfirm}
        onClose={() => {
          setShowIgnoreConfirm(false);
          setIgnoreTarget(null);
        }}
        title="Ignore prerequisite"
        textContent={
          ignoreTarget ? (
            <span>
              A custom schedule expectation already exists for{' '}
              <strong>{ignoreTarget.appointment_type_name}</strong>. It will be deleted and replaced
              with a zero-frequency expectation. Do you want to continue?
            </span>
          ) : undefined
        }
        button={{
          children: 'Confirm',
          color: Color.GREEN,
          disabled: isIgnoring,
          loading: isIgnoring,
          onClick: handleConfirmIgnore
        }}
        dismissButton={{
          children: 'Cancel',
          color: Color.GRAY
        }}
      />
    </div>
  );
};
export default memo(AppointmentType);
